import React, { useState, useEffect, useRef, useCallback } from "react";
import images from "../../../images";
import Header from "../../../components/Header/Header";
import TransactionItem from "./TransactionItem";
import AccountInfo from "./AccountInfo";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetTopUpHistoryQuery } from "../../../services/apiSlice";
import { DATE_FORMATS } from "../../../contanst/dateTimeConstants";
import { convertToSingaporeTime } from "../../../Utils/helper";

const HistoryTopUp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [transactions, setTransactions] = useState([]);

  const PAGE_SIZE = 10;

  const {
    data: topUpHistory,
    isLoading,
    isFetching,
    error,
  } = useGetTopUpHistoryQuery({ page: page, limit: PAGE_SIZE, order: "DESC" });

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  useEffect(() => {
    if (topUpHistory?.data) {
      if (topUpHistory.data.length === 0) {
        setHasMore(false);
        return;
      }
      if (page === 1) {
        setTransactions(topUpHistory.data);
      } else {
        setTransactions((prev) => {
          const newData = topUpHistory.data.filter(
            (newItem) => !prev.some((oldItem) => oldItem.id === newItem.id)
          );
          return [...prev, ...newData];
        });
      }
      setHasMore(topUpHistory.data.length === PAGE_SIZE);
    }
  }, [topUpHistory, page]);
  const lastTransactionElementRef = useCallback(
    (node) => {
      if (isLoading || isFetching || !hasMore) return;

      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetching, hasMore]
  );

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.loading")}
      </div>
    );
  }

  if (error && page === 1) {
    return (
      <div className="flex justify-center items-center h-screen">
        {t("base.errorLoadingData")}
      </div>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div>
      <Header
        backIcon={images.BackIcon}
        title={t("profile.topUpHistory.title")}
        onBackClick={handleBack}
      />

      {transactions.length === 0 && !isLoading && !isFetching ? (
        <div className="flex justify-center items-center h-screen">
          {t("profile.topUpHistory.noTopUpHistory")}
        </div>
      ) : (
        <>
          {transactions.map((group, groupIndex) => {
            const isLastElement = groupIndex === transactions.length - 1;
            return (
              <div
                key={groupIndex}
                ref={isLastElement ? lastTransactionElementRef : null}
                className="flex justify-center w-full"
              >
                <div className="flex m-4 flex-col justify-center p-2.5 bg-white rounded-xl border border-solid border-stone-200 w-[400px] mb-4">
                  <div className="w-full flex gap-0.5 items-center justify-between self-start text-xs leading-none text-black">
                    <div className="flex gap-1 items-center">
                      <img
                        loading="lazy"
                        src={images.ClockCircleIcon}
                        className="object-contain shrink-0 self-stretch my-auto w-5 aspect-square"
                        alt="clock"
                      />
                      <div className="self-stretch my-auto">
                        {" "}
                        {convertToSingaporeTime(
                          group.created_at,
                          DATE_FORMATS.DEFAULT
                        )}
                      </div>
                    </div>

                    {group.status === "APPROVED" ? (
                      <div className="py-1 px-2 bg-green-500 rounded-lg text-white text-xs flex items-center">
                        <span>{t("base.approved")}</span>
                      </div>
                    ) : group.status === "REJECTED" ? (
                      <div className="py-1 px-2 bg-red-500 rounded-lg text-white text-xs flex items-center">
                        <span>{t("base.rejected")}</span>
                      </div>
                    ) : (
                      <div className="py-1 px-2 bg-yellow-500 rounded-lg text-black text-xs flex items-center">
                        <span>{t("base.pending")}</span>
                      </div>
                    )}
                  </div>
                  <TransactionItem
                    label={t("profile.topUp.topUpAmount")}
                    amount={group.topup_amount}
                    icon={images.FlagImage}
                  />
                  <AccountInfo
                    accountNumber={group?.from_account || "+65 1234 5678"}
                    bankLogo={images.PayNowImage}
                  />
                </div>
              </div>
            );
          })}

          {(isFetching || (isLoading && page > 1)) && (
            <div
              ref={loadingRef}
              className="flex justify-center items-center py-4"
            >
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-2 text-sm text-gray-600">
                {t("base.loading")}
              </span>
            </div>
          )}

          {hasMore && transactions.length > 0 && (
            <div ref={lastTransactionElementRef} className="h-1"></div>
          )}
        </>
      )}
    </div>
  );
};

export default HistoryTopUp;
